import { useMemo } from "react";
import { PlannerTask, TaskHealthStatus, DayCapacity } from "./plannerUtils";
import { TaskHealthIndicator } from "./TaskHealthIndicator";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Clock, Timer } from "lucide-react";

interface PlannerTaskBarProps {
  task: PlannerTask;
  onClick?: (task: PlannerTask) => void;
  dayWidth: number;
  dayCapacities: DayCapacity[];
}

const priorityColors: Record<string, string> = {
  low: "border-l-blue-500",
  medium: "border-l-yellow-500",
  high: "border-l-orange-500",
  critical: "border-l-red-500",
};

const healthBgColors: Record<TaskHealthStatus, string> = {
  completed: "bg-muted hover:bg-muted/80",
  on_track: "bg-card hover:bg-accent/50",
  at_risk: "bg-yellow-500/5 hover:bg-yellow-500/10",
  critical: "bg-red-500/5 hover:bg-red-500/10",
  overdue: "bg-red-700/5 hover:bg-red-700/10",
};

export function PlannerTaskBar({ task, onClick, dayWidth, dayCapacities }: PlannerTaskBarProps) {
  const width = task.spanDays * dayWidth;
  const leftOffset = task.startColumn * dayWidth;
  const isCompact = width < 200;
  const isVeryCompact = width < 100;

  const progressPercentage = task.estimated_hours && task.estimated_hours > 0
    ? Math.min(100, ((task.actual_hours || 0) / task.estimated_hours) * 100)
    : 0;

  // Calculate day-off segments within this task bar
  const dayOffSegments = useMemo(() => {
    const segments: { left: number; width: number }[] = [];
    const endColumn = task.startColumn + task.spanDays - 1;

    for (let i = task.startColumn; i <= endColumn; i++) {
      if (dayCapacities[i] && !dayCapacities[i].isWorkingDay) {
        // Calculate position relative to task bar
        const segmentLeft = (i - task.startColumn) * dayWidth;
        segments.push({ left: segmentLeft, width: dayWidth });
      }
    }

    return segments;
  }, [task.startColumn, task.spanDays, dayCapacities, dayWidth]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "absolute h-[52px] rounded-md border border-l-4 cursor-pointer transition-all shadow-sm",
              priorityColors[task.priority] || "border-l-muted-foreground",
              healthBgColors[task.health.status],
              "flex flex-col justify-center px-2 overflow-hidden"
            )}
            style={{
              left: `${leftOffset}px`,
              width: `${width - 4}px`,
              top: `${task.row * 60 + 4}px`,
            }}
            onClick={() => onClick?.(task)}
          >
            {/* Day-off stripe overlays */}
            {dayOffSegments.map((segment, idx) => (
              <div
                key={idx}
                className="absolute inset-y-0 rounded-md"
                style={{
                  left: segment.left,
                  width: segment.width,
                  background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(0,0,0,0.05) 3px, rgba(0,0,0,0.05) 6px)',
                }}
              />
            ))}

            {/* Title row */}
            <div className="flex items-center gap-1.5 min-w-0">
              {task.reference_id && !isVeryCompact && (
                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 shrink-0">
                  {task.reference_id}
                </Badge>
              )}
              <span className="text-sm font-medium truncate flex-1 min-w-0">
                {task.title}
              </span>
              <TaskHealthIndicator health={task.health} size="sm" />
            </div>

            {/* Hours row */}
            {!isVeryCompact && (
              <div className="flex items-center gap-2 mt-0.5">
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>Est: {task.estimated_hours || 0}h</span>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Timer className="h-3 w-3" />
                  <span>Act: {(task.actual_hours || 0).toFixed(1)}h</span>
                </div>
                {!isCompact && (
                  <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden ml-1">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all",
                        progressPercentage >= 100 ? "bg-green-500" : "bg-primary"
                      )}
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm">
          <div className="space-y-2">
            <div>
              {task.reference_id && (
                <Badge variant="outline" className="text-xs mb-1">
                  {task.reference_id}
                </Badge>
              )}
              <p className="font-medium">{task.title}</p>
              {task.description && (
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {task.description}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-muted-foreground">Period:</span>
              <span>
                {task.start_date ? format(new Date(task.start_date), "MMM d") : '?'} - {task.end_date ? format(new Date(task.end_date), "MMM d") : '?'}
              </span>

              <span className="text-muted-foreground">Estimated:</span>
              <span>{task.estimated_hours || 0}h</span>

              <span className="text-muted-foreground">Logged:</span>
              <span>{(task.actual_hours || 0).toFixed(1)}h</span>

              <span className="text-muted-foreground">Progress:</span>
              <span>{progressPercentage.toFixed(0)}%</span>

              <span className="text-muted-foreground">Priority:</span>
              <span className="capitalize">{task.priority}</span>

              <span className="text-muted-foreground">Status:</span>
              <span className="capitalize">{task.phase_status.replace('_', ' ')}</span>
            </div>

            <div className="pt-1 border-t">
              <TaskHealthIndicator health={task.health} size="md" />
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
